import {
  forwardRef,
  useRef,
  useState,
  type PointerEvent,
  type Ref,
} from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysLocaleMessages,
} from '../../provider/context';
import { resolveBaseAppearance } from './theme';
import type { BasePasswordInputProps } from './types';
import { cn } from './utils';

function setRefs<TElement>(
  element: TElement | null,
  ...refs: Array<Ref<TElement> | undefined>
) {
  for (const ref of refs) {
    if (!ref) continue;
    if (typeof ref === 'function') {
      ref(element);
    } else {
      (ref as { current: TElement | null }).current = element;
    }
  }
}

function validateLabel(name: string, value: string) {
  if (!value.trim()) {
    throw new TypeError(`BasePasswordInput ${name} must be a non-empty string.`);
  }
}

export const BasePasswordInput = forwardRef<
  HTMLInputElement,
  BasePasswordInputProps
>(function BasePasswordInput(
  {
    visible,
    defaultVisible = false,
    onVisibleChange,
    showPasswordLabel,
    hidePasswordLabel,
    invalid = false,
    disabled = false,
    readOnly = false,
    appearance,
    className,
    classNames,
    style,
    'aria-invalid': nativeAriaInvalid,
    ...inputProps
  },
  forwardedRef,
) {
  for (const [name, candidate] of [
    ['defaultVisible', defaultVisible],
    ['invalid', invalid],
    ['disabled', disabled],
    ['readOnly', readOnly],
  ] as const) {
    if (typeof candidate !== 'boolean') {
      throw new TypeError(`BasePasswordInput ${name} must be a boolean.`);
    }
  }
  if (visible !== undefined && typeof visible !== 'boolean') {
    throw new TypeError('BasePasswordInput visible must be a boolean.');
  }

  const messages = useMonkeysLocaleMessages();
  const resolvedShowLabel =
    showPasswordLabel ?? messages.passwordInput.showPassword;
  const resolvedHideLabel =
    hidePasswordLabel ?? messages.passwordInput.hidePassword;
  validateLabel('showPasswordLabel', resolvedShowLabel);
  validateLabel('hidePasswordLabel', resolvedHideLabel);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uncontrolledVisible, setUncontrolledVisible] =
    useState(defaultVisible);
  const isControlled = visible !== undefined;
  const resolvedVisible = isControlled ? visible : uncontrolledVisible;
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);

  const handleTogglePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    const input = inputRef.current;
    if (input && input.ownerDocument.activeElement === input) {
      event.preventDefault();
    }
  };

  const handleToggle = () => {
    if (disabled) return;
    const nextVisible = !resolvedVisible;
    if (!isControlled) setUncontrolledVisible(nextVisible);
    onVisibleChange?.(nextVisible);
  };

  const toggleLabel = resolvedVisible
    ? resolvedHideLabel
    : resolvedShowLabel;

  return (
    <div
      {...componentAttributes}
      data-monkeys-component="password-input"
      data-slot="root"
      data-visible={resolvedVisible || undefined}
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
      data-readonly={readOnly || undefined}
      className={cn(
        theme.slots.controlWrapper,
        invalid && theme.slots.controlInvalid,
        disabled && theme.slots.controlDisabled,
        readOnly && theme.slots.controlReadOnly,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.control,
        ...(invalid ? theme.styles.controlInvalid : undefined),
        ...(disabled ? theme.styles.controlDisabled : undefined),
        ...style,
      }}
    >
      <input
        {...inputProps}
        ref={(element) => setRefs(element, inputRef, forwardedRef)}
        type={resolvedVisible ? 'text' : 'password'}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={nativeAriaInvalid ?? (invalid || undefined)}
        data-slot="control"
        className={cn(
          theme.slots.controlBase,
          theme.slots.controlInput,
          theme.slots.passwordInputControl,
          classNames?.control,
        )}
      />
      <button
        type="button"
        aria-label={toggleLabel}
        aria-pressed={resolvedVisible}
        disabled={disabled}
        data-slot="toggle"
        data-state={resolvedVisible ? 'visible' : 'hidden'}
        className={cn(
          theme.slots.passwordInputToggle,
          classNames?.toggle,
        )}
        style={theme.styles.passwordInputToggle}
        onPointerDown={handleTogglePointerDown}
        onClick={handleToggle}
      >
        {toggleLabel}
      </button>
    </div>
  );
});
